// Brand Console -- AI Research Data.
//
// Makes the underlying AI research data inspectable, not just aggregate
// scores. Consumes ONLY two already-existing, already-real, tenant-scoped
// endpoints (server.py's v2_execution_history / v2_citation_history) --
// no new backend endpoint, no new service, no new collection. Both
// routes were confirmed by direct read to already accept every filter
// this page uses as an optional query param and to already enforce
// get_verified_client tenant/client isolation, exactly like every other
// call in this file's sibling API objects.
//
// db.sources (the global, non-tenant-scoped public URL registry) is
// deliberately never queried directly here -- source/domain information
// is shown exactly as the citation record itself already carries it
// (cited_url/cited_domain/title), never presented as if it were the
// brand's own private data.
//
// `promptMap` is a best-effort convenience only: it resolves a
// Search-Everywhere prompt_id to its real prompt_text via the existing
// GET /v2/prompts/{client_id} route. A SoM-check-sourced ai_response's
// prompt_id lives in a different collection (db.som_prompts) this route
// does not read, so it will not resolve -- the fallback is the raw,
// real prompt_id itself, never an invented label.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { EmptyState, ErrorState, LoadingState } from '@/components/brand/StateViews';
import { StatusBadge } from '@/components/brand/Badges';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowSquareOut } from '@phosphor-icons/react';
import { aiResponsesAPI, citationsAPI, intelligenceAPI } from '@/lib/api';
import { WorkspaceSelect } from '@/components/brand/BrandContextBar';
import { useWorkspaceSelection } from '@/hooks/useWorkspaceSelection';

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}
// Honest empty-value rendering throughout this page: null/undefined/""
// -> "—", never a fabricated 0/"No"/default. A real boolean false is
// still rendered as its own real value ("No"), never conflated with
// "unknown" -- see boolLabel below, only ever called on a field the
// backend actually returned as a boolean.
function dash(v) {
  return v === null || v === undefined || v === '' ? '—' : v;
}
function boolLabel(v) {
  if (v === null || v === undefined) return '—';
  return v ? 'Yes' : 'No';
}
function truncate(s, n) {
  if (!s) return '—';
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

function sortRows(rows, accessor, dir) {
  const copy = [...rows];
  copy.sort((a, b) => {
    const av = accessor(a);
    const bv = accessor(b);
    if (av === bv) return 0;
    if (av === null || av === undefined) return 1;
    if (bv === null || bv === undefined) return -1;
    const cmp = av < bv ? -1 : 1;
    return dir === 'asc' ? cmp : -cmp;
  });
  return copy;
}

const RESPONSE_ACCESSORS = {
  created_at: (r) => r.created_at,
  market: (r) => r.market,
  language: (r) => r.language,
  provider: (r) => r.provider,
  target_brand_mentioned: (r) => r.target_brand_mentioned,
  target_brand_position: (r) => r.target_brand_position,
  competitors_mentioned: (r) => r.competitors_mentioned,
  status: (r) => r.request_metadata?.status,
  results_count: (r) => r.request_metadata?.results_count,
};

const CITATION_ACCESSORS = {
  created_at: (r) => r.created_at,
  cited_domain: (r) => r.cited_domain,
  citation_type: (r) => r.citation_type,
  position: (r) => r.position,
  brand_mentioned: (r) => r.brand_mentioned,
};

function SortHeader({ label, col, sort, onSort, testId }) {
  return (
    <th className="px-3 py-2 cursor-pointer select-none whitespace-nowrap" onClick={() => onSort(col)} data-testid={testId}>
      {label}{sort.key === col ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );
}

export default function AIRawDataPage() {
  // P1.9: client resolution is now the shared useWorkspaceSelection() hook
  // (see SignalsPage.js's own comment for the full rationale) instead of
  // an unswitchable first-client fetch.
  const { clients, workspaceId, setWorkspaceId, loadingClients } = useWorkspaceSelection();
  const clientId = workspaceId || null;
  const noClient = !loadingClients && clients.length === 0;
  const [tab, setTab] = useState('responses'); // 'responses' | 'citations'
  const [promptMap, setPromptMap] = useState({});

  const [responseFilters, setResponseFilters] = useState({ prompt_id: '', market: '', language: '', provider: '', limit: 20 });
  const [appliedResponseFilters, setAppliedResponseFilters] = useState({ prompt_id: '', market: '', language: '', provider: '', limit: 20 });
  const [responses, setResponses] = useState(null); // null = loading
  const [responsesTotal, setResponsesTotal] = useState(0);
  const [responsesError, setResponsesError] = useState(null);
  const [responseSort, setResponseSort] = useState({ key: 'created_at', dir: 'desc' });
  const [selectedResponse, setSelectedResponse] = useState(null);

  const [citationFilters, setCitationFilters] = useState({ prompt_id: '', domain: '', limit: 50 });
  const [appliedCitationFilters, setAppliedCitationFilters] = useState({ prompt_id: '', domain: '', limit: 50 });
  const [citations, setCitations] = useState(null);
  const [citationsTotal, setCitationsTotal] = useState(0);
  const [citationsError, setCitationsError] = useState(null);
  const [citationSort, setCitationSort] = useState({ key: 'created_at', dir: 'desc' });
  const [selectedCitation, setSelectedCitation] = useState(null);

  // Best-effort prompt_id -> prompt_text resolution (see module docstring).
  // Reset before fetching so a stale prompt_id -> text mapping from the
  // previously-selected client is never briefly consulted while switching.
  useEffect(() => {
    if (!clientId) return;
    setPromptMap({});
    intelligenceAPI.prompts(clientId).then((res) => {
      const map = {};
      (res.data || []).forEach((p) => { if (p?.id) map[p.id] = p.prompt_text; });
      setPromptMap(map);
    }).catch(() => setPromptMap({}));
  }, [clientId]);

  const buildParams = (filters) => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) params[k] = v;
    });
    return params;
  };

  const loadResponses = useCallback((filters) => {
    if (!clientId) return;
    setResponses(null);
    setResponsesError(null);
    aiResponsesAPI.list(clientId, buildParams(filters))
      .then((res) => { setResponses(res.data?.results || []); setResponsesTotal(res.data?.total ?? 0); })
      .catch((e) => setResponsesError(e));
  }, [clientId]);

  const loadCitations = useCallback((filters) => {
    if (!clientId) return;
    setCitations(null);
    setCitationsError(null);
    citationsAPI.list(clientId, buildParams(filters))
      .then((res) => { setCitations(res.data?.results || []); setCitationsTotal(res.data?.total ?? 0); })
      .catch((e) => setCitationsError(e));
  }, [clientId]);

  useEffect(() => { if (clientId && tab === 'responses') loadResponses(appliedResponseFilters); }, [clientId, tab]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (clientId && tab === 'citations') loadCitations(appliedCitationFilters); }, [clientId, tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyResponseFilters = (e) => {
    e.preventDefault();
    setAppliedResponseFilters(responseFilters);
    loadResponses(responseFilters);
  };
  const clearResponseFilters = () => {
    const cleared = { prompt_id: '', market: '', language: '', provider: '', limit: 20 };
    setResponseFilters(cleared);
    setAppliedResponseFilters(cleared);
    loadResponses(cleared);
  };

  const applyCitationFilters = (e) => {
    e.preventDefault();
    setAppliedCitationFilters(citationFilters);
    loadCitations(citationFilters);
  };
  const clearCitationFilters = () => {
    const cleared = { prompt_id: '', domain: '', limit: 50 };
    setCitationFilters(cleared);
    setAppliedCitationFilters(cleared);
    loadCitations(cleared);
  };

  const toggleResponseSort = (col) => {
    setResponseSort((s) => (s.key === col ? { key: col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: col, dir: 'desc' }));
  };
  const toggleCitationSort = (col) => {
    setCitationSort((s) => (s.key === col ? { key: col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: col, dir: 'desc' }));
  };

  const sortedResponses = useMemo(() => {
    if (!responses) return [];
    const accessor = RESPONSE_ACCESSORS[responseSort.key] || RESPONSE_ACCESSORS.created_at;
    return sortRows(responses, accessor, responseSort.dir);
  }, [responses, responseSort]);

  const sortedCitations = useMemo(() => {
    if (!citations) return [];
    const accessor = CITATION_ACCESSORS[citationSort.key] || CITATION_ACCESSORS.created_at;
    return sortRows(citations, accessor, citationSort.dir);
  }, [citations, citationSort]);

  const promptLabel = (promptId) => (promptId && promptMap[promptId]) || promptId || null;

  return (
    <DashboardLayout>
      <div data-testid="ai-raw-data-page" className="max-w-6xl mx-auto">
        <div className="mb-4">
          <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">AI Research Data</h1>
          <p className="text-[12px] text-[var(--text-secondary)]">Inspect the real AI responses and citations Martian uses to understand your brand's visibility.</p>
        </div>

        {!noClient && clients.length > 0 && (
          <div className="mb-4">
            <WorkspaceSelect clients={clients} workspaceId={workspaceId} setWorkspaceId={setWorkspaceId} />
          </div>
        )}

        {noClient && <EmptyState title="ADD A BRAND FIRST" description="Raw AI data appears once Martian has a workspace and has run real checks." />}

        {!noClient && (
          <>
            <div className="flex items-center gap-1 mb-4 border-b border-[var(--border)]" data-testid="ai-raw-data-tabs">
              <button
                type="button" data-testid="ai-raw-data-tab-responses"
                onClick={() => setTab('responses')}
                className={`px-3 py-2 text-[12.5px] font-medium border-b-2 -mb-px ${tab === 'responses' ? 'border-[var(--accent)] text-[var(--text-primary)]' : 'border-transparent text-[var(--text-tertiary)]'}`}
              >
                AI Responses
              </button>
              <button
                type="button" data-testid="ai-raw-data-tab-citations"
                onClick={() => setTab('citations')}
                className={`px-3 py-2 text-[12.5px] font-medium border-b-2 -mb-px ${tab === 'citations' ? 'border-[var(--accent)] text-[var(--text-primary)]' : 'border-transparent text-[var(--text-tertiary)]'}`}
              >
                Citations
              </button>
            </div>

            {tab === 'responses' && (
              <div data-testid="ai-responses-panel">
                <form onSubmit={applyResponseFilters} className="flex items-end gap-2 flex-wrap mb-4" data-testid="ai-responses-filters">
                  <div>
                    <label className="block text-[10px] text-[var(--text-tertiary)] mb-1">Prompt ID</label>
                    <Input data-testid="filter-response-prompt-id" className="h-8 w-[160px] text-[12px]" value={responseFilters.prompt_id} onChange={(e) => setResponseFilters((f) => ({ ...f, prompt_id: e.target.value }))} placeholder="prompt_id" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-[var(--text-tertiary)] mb-1">Market</label>
                    <Input data-testid="filter-response-market" className="h-8 w-[120px] text-[12px]" value={responseFilters.market} onChange={(e) => setResponseFilters((f) => ({ ...f, market: e.target.value }))} placeholder="e.g. AE" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-[var(--text-tertiary)] mb-1">Language</label>
                    <Input data-testid="filter-response-language" className="h-8 w-[100px] text-[12px]" value={responseFilters.language} onChange={(e) => setResponseFilters((f) => ({ ...f, language: e.target.value }))} placeholder="e.g. en" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-[var(--text-tertiary)] mb-1">Provider</label>
                    <Input data-testid="filter-response-provider" className="h-8 w-[120px] text-[12px]" value={responseFilters.provider} onChange={(e) => setResponseFilters((f) => ({ ...f, provider: e.target.value }))} placeholder="e.g. tavily" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-[var(--text-tertiary)] mb-1">Limit</label>
                    <Input data-testid="filter-response-limit" type="number" min="1" max="200" className="h-8 w-[80px] text-[12px]" value={responseFilters.limit} onChange={(e) => setResponseFilters((f) => ({ ...f, limit: e.target.value ? Number(e.target.value) : '' }))} />
                  </div>
                  <Button type="submit" size="sm" data-testid="apply-response-filters" className="h-8 text-[12px]">Apply</Button>
                  <Button type="button" variant="outline" size="sm" data-testid="clear-response-filters" className="h-8 text-[12px]" onClick={clearResponseFilters}>Clear filters</Button>
                </form>

                {responses === null && !responsesError && <LoadingState testId="ai-responses-loading" />}
                {responsesError && <ErrorState testId="ai-responses-error" error={responsesError} onRetry={() => loadResponses(appliedResponseFilters)} />}
                {responses !== null && !responsesError && responses.length === 0 && (
                  <EmptyState testId="ai-responses-empty" title="No AI responses yet" description="Martian has not recorded any AI research checks matching these filters." />
                )}
                {responses !== null && !responsesError && responses.length > 0 && (
                  <>
                    <p className="text-[10px] text-[var(--text-tertiary)] mb-1.5">{responsesTotal} record{responsesTotal === 1 ? '' : 's'} returned (limit {appliedResponseFilters.limit})</p>
                    <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] overflow-x-auto" data-testid="ai-responses-table">
                      <table className="w-full text-[11px]">
                        <thead>
                          <tr className="border-b border-[var(--border)] text-left text-[var(--text-tertiary)]">
                            <SortHeader label="Date / Time" col="created_at" sort={responseSort} onSort={toggleResponseSort} testId="sort-response-created_at" />
                            <th className="px-3 py-2">Prompt</th>
                            <SortHeader label="Market" col="market" sort={responseSort} onSort={toggleResponseSort} testId="sort-response-market" />
                            <SortHeader label="Language" col="language" sort={responseSort} onSort={toggleResponseSort} testId="sort-response-language" />
                            <SortHeader label="Provider / Model" col="provider" sort={responseSort} onSort={toggleResponseSort} testId="sort-response-provider" />
                            <SortHeader label="Brand Mention" col="target_brand_mentioned" sort={responseSort} onSort={toggleResponseSort} testId="sort-response-mentioned" />
                            <SortHeader label="Brand Position" col="target_brand_position" sort={responseSort} onSort={toggleResponseSort} testId="sort-response-position" />
                            <th className="px-3 py-2">Recommended</th>
                            <SortHeader label="Competitors" col="competitors_mentioned" sort={responseSort} onSort={toggleResponseSort} testId="sort-response-competitors" />
                            <SortHeader label="Status" col="status" sort={responseSort} onSort={toggleResponseSort} testId="sort-response-status" />
                            <SortHeader label="Results" col="results_count" sort={responseSort} onSort={toggleResponseSort} testId="sort-response-results-count" />
                            <th className="px-3 py-2">Execution ID</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedResponses.map((r) => (
                            <tr
                              key={r.id} data-testid={`ai-response-row-${r.id}`}
                              className="border-b border-[var(--border-light,var(--border))] last:border-b-0 cursor-pointer hover:bg-[var(--surface-sunken,var(--canvas-secondary))] focus-within:bg-[var(--surface-sunken,var(--canvas-secondary))]"
                              onClick={() => setSelectedResponse(r)}
                              tabIndex={0}
                              onKeyDown={(e) => { if (e.key === 'Enter') setSelectedResponse(r); }}
                            >
                              <td className="px-3 py-2 text-[var(--text-secondary)] font-mono whitespace-nowrap">{formatDateTime(r.created_at)}</td>
                              <td className="px-3 py-2 text-[var(--text-primary)]" title={promptLabel(r.prompt_id) || ''}>{truncate(promptLabel(r.prompt_id), 40)}</td>
                              <td className="px-3 py-2 text-[var(--text-secondary)]">{dash(r.market)}</td>
                              <td className="px-3 py-2 text-[var(--text-secondary)]">{dash(r.language)}</td>
                              <td className="px-3 py-2 text-[var(--text-secondary)] font-mono">{dash(r.provider)}{r.model ? ` / ${r.model}` : ''}</td>
                              <td className="px-3 py-2">{boolLabel(r.target_brand_mentioned)}</td>
                              <td className="px-3 py-2 text-[var(--text-secondary)]">{dash(r.target_brand_position)}</td>
                              <td className="px-3 py-2">{boolLabel(r.target_brand_recommended)}</td>
                              <td className="px-3 py-2 text-[var(--text-secondary)]">{dash(r.competitors_mentioned)}</td>
                              <td className="px-3 py-2">{r.request_metadata?.status ? <StatusBadge value={r.request_metadata.status} /> : '—'}</td>
                              <td className="px-3 py-2 text-[var(--text-secondary)]">{dash(r.request_metadata?.results_count)}</td>
                              <td className="px-3 py-2 text-[var(--text-tertiary)] font-mono">{truncate(r.request_metadata?.execution_id, 12)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}

            {tab === 'citations' && (
              <div data-testid="citations-panel">
                <form onSubmit={applyCitationFilters} className="flex items-end gap-2 flex-wrap mb-4" data-testid="citations-filters">
                  <div>
                    <label className="block text-[10px] text-[var(--text-tertiary)] mb-1">Prompt ID</label>
                    <Input data-testid="filter-citation-prompt-id" className="h-8 w-[160px] text-[12px]" value={citationFilters.prompt_id} onChange={(e) => setCitationFilters((f) => ({ ...f, prompt_id: e.target.value }))} placeholder="prompt_id" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-[var(--text-tertiary)] mb-1">Domain</label>
                    <Input data-testid="filter-citation-domain" className="h-8 w-[160px] text-[12px]" value={citationFilters.domain} onChange={(e) => setCitationFilters((f) => ({ ...f, domain: e.target.value }))} placeholder="e.g. example.com" />
                  </div>
                  <div>
                    <label className="block text-[10px] text-[var(--text-tertiary)] mb-1">Limit</label>
                    <Input data-testid="filter-citation-limit" type="number" min="1" max="200" className="h-8 w-[80px] text-[12px]" value={citationFilters.limit} onChange={(e) => setCitationFilters((f) => ({ ...f, limit: e.target.value ? Number(e.target.value) : '' }))} />
                  </div>
                  <Button type="submit" size="sm" data-testid="apply-citation-filters" className="h-8 text-[12px]">Apply</Button>
                  <Button type="button" variant="outline" size="sm" data-testid="clear-citation-filters" className="h-8 text-[12px]" onClick={clearCitationFilters}>Clear filters</Button>
                </form>

                {citations === null && !citationsError && <LoadingState testId="citations-loading" />}
                {citationsError && <ErrorState testId="citations-error" error={citationsError} onRetry={() => loadCitations(appliedCitationFilters)} />}
                {citations !== null && !citationsError && citations.length === 0 && (
                  <EmptyState testId="citations-empty" title="No citations yet" description="Martian has not recorded any citations matching these filters." />
                )}
                {citations !== null && !citationsError && citations.length > 0 && (
                  <>
                    <p className="text-[10px] text-[var(--text-tertiary)] mb-1.5">{citationsTotal} record{citationsTotal === 1 ? '' : 's'} returned (limit {appliedCitationFilters.limit})</p>
                    <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] overflow-x-auto" data-testid="citations-table">
                      <table className="w-full text-[11px]">
                        <thead>
                          <tr className="border-b border-[var(--border)] text-left text-[var(--text-tertiary)]">
                            <SortHeader label="Date / Time" col="created_at" sort={citationSort} onSort={toggleCitationSort} testId="sort-citation-created_at" />
                            <SortHeader label="Domain" col="cited_domain" sort={citationSort} onSort={toggleCitationSort} testId="sort-citation-domain" />
                            <th className="px-3 py-2">URL</th>
                            <th className="px-3 py-2">Title</th>
                            <SortHeader label="Type" col="citation_type" sort={citationSort} onSort={toggleCitationSort} testId="sort-citation-type" />
                            <SortHeader label="Position" col="position" sort={citationSort} onSort={toggleCitationSort} testId="sort-citation-position" />
                            <SortHeader label="Brand Mentioned" col="brand_mentioned" sort={citationSort} onSort={toggleCitationSort} testId="sort-citation-brand" />
                            <th className="px-3 py-2">Competitor Mentioned</th>
                            <th className="px-3 py-2">Related Prompt</th>
                            <th className="px-3 py-2">Related AI Response</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedCitations.map((c) => (
                            <tr
                              key={c.id} data-testid={`citation-row-${c.id}`}
                              className="border-b border-[var(--border-light,var(--border))] last:border-b-0 cursor-pointer hover:bg-[var(--surface-sunken,var(--canvas-secondary))] focus-within:bg-[var(--surface-sunken,var(--canvas-secondary))]"
                              onClick={() => setSelectedCitation(c)}
                              tabIndex={0}
                              onKeyDown={(e) => { if (e.key === 'Enter') setSelectedCitation(c); }}
                            >
                              <td className="px-3 py-2 text-[var(--text-secondary)] font-mono whitespace-nowrap">{formatDateTime(c.created_at)}</td>
                              <td className="px-3 py-2 text-[var(--text-primary)] font-mono">{dash(c.cited_domain)}</td>
                              <td className="px-3 py-2 text-[var(--text-tertiary)] font-mono" title={c.cited_url || ''}>{truncate(c.cited_url, 36)}</td>
                              <td className="px-3 py-2 text-[var(--text-secondary)]" title={c.title || ''}>{truncate(c.title, 30)}</td>
                              <td className="px-3 py-2 text-[var(--text-secondary)]">{dash(c.citation_type)}</td>
                              <td className="px-3 py-2 text-[var(--text-secondary)]">{dash(c.position)}</td>
                              <td className="px-3 py-2">{boolLabel(c.brand_mentioned)}</td>
                              <td className="px-3 py-2">{boolLabel(c.competitor_mentioned)}</td>
                              <td className="px-3 py-2 text-[var(--text-tertiary)]" title={promptLabel(c.prompt_id) || ''}>{truncate(promptLabel(c.prompt_id), 24)}</td>
                              <td className="px-3 py-2 text-[var(--text-tertiary)] font-mono">{truncate(c.ai_response_id, 12)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}
          </>
        )}

        {/* ── AI response detail ── */}
        <Sheet open={!!selectedResponse} onOpenChange={(o) => !o && setSelectedResponse(null)}>
          <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto bg-[var(--surface)] border-[var(--border)]" data-testid="ai-response-detail">
            {selectedResponse && (
              <>
                <SheetHeader>
                  <SheetTitle className="text-[var(--text-primary)] text-[14px]">AI Response</SheetTitle>
                  <SheetDescription className="text-[var(--text-tertiary)] text-[11px]">The real, stored record Martian used for this check.</SheetDescription>
                </SheetHeader>
                <div className="mt-3 space-y-3 text-[12px]">
                  <div>
                    <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Prompt</p>
                    <p className="text-[var(--text-primary)]" data-testid="detail-response-prompt">{promptLabel(selectedResponse.prompt_id) || dash(selectedResponse.prompt_id)}</p>
                    {promptMap[selectedResponse.prompt_id] && <p className="text-[10px] font-mono text-[var(--text-tertiary)] mt-0.5">ID: {selectedResponse.prompt_id}</p>}
                  </div>
                  <Separator className="bg-[var(--border)]" />
                  <div>
                    <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Response</p>
                    <p className="text-[var(--text-primary)] whitespace-pre-wrap" data-testid="detail-response-text">{dash(selectedResponse.response_text)}</p>
                  </div>
                  <Separator className="bg-[var(--border)]" />
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Provider</p><p className="text-[var(--text-primary)] font-mono">{dash(selectedResponse.provider)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Model</p><p className="text-[var(--text-primary)] font-mono">{dash(selectedResponse.model)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Market</p><p className="text-[var(--text-primary)]">{dash(selectedResponse.market)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Language</p><p className="text-[var(--text-primary)]">{dash(selectedResponse.language)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Latency</p><p className="text-[var(--text-primary)]">{selectedResponse.latency_ms !== null && selectedResponse.latency_ms !== undefined ? `${selectedResponse.latency_ms} ms` : '—'}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Created</p><p className="text-[var(--text-primary)]">{formatDateTime(selectedResponse.created_at)}</p></div>
                  </div>
                  <Separator className="bg-[var(--border)]" />
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Brand</p><p className="text-[var(--text-primary)]">{dash(selectedResponse.target_brand)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Brand mentioned</p><p className="text-[var(--text-primary)]">{boolLabel(selectedResponse.target_brand_mentioned)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Brand position</p><p className="text-[var(--text-primary)]">{dash(selectedResponse.target_brand_position)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Brand recommended</p><p className="text-[var(--text-primary)]">{boolLabel(selectedResponse.target_brand_recommended)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Competitors mentioned</p><p className="text-[var(--text-primary)]">{dash(selectedResponse.competitors_mentioned)}</p></div>
                  </div>
                  {Array.isArray(selectedResponse.competitor_results) && selectedResponse.competitor_results.length > 0 && (
                    <>
                      <Separator className="bg-[var(--border)]" />
                      <div>
                        <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)] mb-1">Competitor results</p>
                        <div className="space-y-1.5" data-testid="detail-competitor-results">
                          {selectedResponse.competitor_results.map((c, i) => (
                            <div key={i} className="flex items-center justify-between text-[11px] bg-[var(--surface-sunken,var(--canvas-secondary))] rounded px-2 py-1">
                              <span className="text-[var(--text-primary)]">{dash(c.name)}</span>
                              <span className="text-[var(--text-tertiary)]">{c.mentioned ? 'Mentioned' : 'Not mentioned'}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                  <Separator className="bg-[var(--border)]" />
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Execution ID</p><p className="text-[var(--text-primary)] font-mono break-all" data-testid="detail-execution-id">{dash(selectedResponse.request_metadata?.execution_id)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Status</p><p>{selectedResponse.request_metadata?.status ? <StatusBadge value={selectedResponse.request_metadata.status} /> : '—'}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Results count</p><p className="text-[var(--text-primary)]" data-testid="detail-results-count">{dash(selectedResponse.request_metadata?.results_count)}</p></div>
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>

        {/* ── Citation detail ── */}
        <Sheet open={!!selectedCitation} onOpenChange={(o) => !o && setSelectedCitation(null)}>
          <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto bg-[var(--surface)] border-[var(--border)]" data-testid="citation-detail">
            {selectedCitation && (
              <>
                <SheetHeader>
                  <SheetTitle className="text-[var(--text-primary)] text-[14px]">Citation</SheetTitle>
                  <SheetDescription className="text-[var(--text-tertiary)] text-[11px]">The real, stored citation record.</SheetDescription>
                </SheetHeader>
                <div className="mt-3 space-y-3 text-[12px]">
                  <div>
                    <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Cited URL</p>
                    {selectedCitation.cited_url ? (
                      <a href={selectedCitation.cited_url} target="_blank" rel="noopener noreferrer" className="text-[var(--accent)] break-all inline-flex items-center gap-1" data-testid="detail-cited-url">
                        {selectedCitation.cited_url} <ArrowSquareOut size={12} />
                      </a>
                    ) : <p className="text-[var(--text-primary)]">—</p>}
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Domain</p><p className="text-[var(--text-primary)] font-mono">{dash(selectedCitation.cited_domain)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Title</p><p className="text-[var(--text-primary)]">{dash(selectedCitation.title)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Citation type</p><p className="text-[var(--text-primary)]">{dash(selectedCitation.citation_type)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Position</p><p className="text-[var(--text-primary)]">{dash(selectedCitation.position)}</p></div>
                  </div>
                  <Separator className="bg-[var(--border)]" />
                  <div>
                    <p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Context</p>
                    <p className="text-[var(--text-primary)] whitespace-pre-wrap" data-testid="detail-citation-context">{dash(selectedCitation.context)}</p>
                  </div>
                  <Separator className="bg-[var(--border)]" />
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Brand mentioned</p><p className="text-[var(--text-primary)]">{boolLabel(selectedCitation.brand_mentioned)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Competitor mentioned</p><p className="text-[var(--text-primary)]">{boolLabel(selectedCitation.competitor_mentioned)}</p></div>
                  </div>
                  <Separator className="bg-[var(--border)]" />
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Prompt ID</p><p className="text-[var(--text-primary)] font-mono break-all">{dash(selectedCitation.prompt_id)}</p></div>
                    <div><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">AI response ID</p><p className="text-[var(--text-primary)] font-mono break-all" data-testid="detail-citation-response-id">{dash(selectedCitation.ai_response_id)}</p></div>
                    <div className="col-span-2"><p className="text-[10px] font-mono uppercase text-[var(--text-tertiary)]">Created</p><p className="text-[var(--text-primary)]">{formatDateTime(selectedCitation.created_at)}</p></div>
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </DashboardLayout>
  );
}
